"use strict";

class ConstructorSearchError extends Error {
  constructor(message, code, retryable = false, context = {}) {
    super(message);
    this.name = "ConstructorSearchError";
    this.code = code;
    this.retryable = retryable;
    this.context = context;
    this.timestamp = new Date().toISOString();
  }
}

class NetworkError extends ConstructorSearchError {
  constructor(message, context = {}) {
    super(message, "NETWORK_ERROR", true, context);
    this.name = "NetworkError";
  }
}

class RenderError extends ConstructorSearchError {
  constructor(message, context = {}) {
    super(message, "RENDER_ERROR", false, context);
    this.name = "RenderError";
  }
}

class ValidationError extends ConstructorSearchError {
  constructor(message, context = {}) {
    super(message, "VALIDATION_ERROR", false, context);
    this.name = "ValidationError";
  }
}

function getConstructorSearchInstance() {
  if (!window.constructorSearch) {
    const instance = ConstructorSearch.getInstance();
    if (instance) {
      window.constructorSearch = instance;
      return instance;
    }
    return null;
  }
  return window.constructorSearch;
}

// Performance and rendering constants
const PERFORMANCE_CONFIG = {
  ABOVE_FOLD_THRESHOLD: 4,
  IMAGE_QUALITY_OPTIMIZED: 85,
  BATCH_TIMEOUT_MS: 50,
  RENDER_DELAY_MS: 8,
  MAX_RETRY_DELAY_MS: 2000,
  INITIAL_RETRY_DELAY_MS: 100,
  IDLE_CALLBACK_TIMEOUT: 50,
  BATCH_SIZE_OPTIMIZED: 8,
  INITIAL_BATCH_SIZE_OPTIMIZED: 4,
};

// Image processing constants
const IMAGE_CONFIG = {
  DIMENSIONS: {
    square: { width: 600, height: 600 },
    landscape: { width: 600, height: 480 },
    portrait: { width: 480, height: 600 },
    default: { width: 600, height: 600 },
  },
  FORMATS: {
    WEBP: "webp",
    JPG: "jpg",
  },
  CROP_CENTER: "center",
};

// UI constants
const UI_CLASSES = {
  productItem: "product-item splide__slide",
  imgWrapper: "img-wrapper position-relative mb-4",
  productImg: "product-item-img product-item-img--primary img-fluid rounded png-background",
  productImgSecondary: "product-item-img product-item-img--secondary img-fluid rounded png-background",
  btnAtc: "btn-atc btn btn-md w-100 btn-primary",
  btnAtcDesktop: "btn-atc btn btn-md dropdown-toggle btn-primary w-200",
  containerAtc: "btn-atc-container",
  containerMobile: "mobile-view d-block d-lg-none",
  dropdownMenu: "dropdown-menu shadow",
  productTitle: "product-item-title mb-2",
  productPrice: "product-item-price product-item-price-carrossel mb-2",
  placeholderImage: "placeholder-image bg-light d-flex align-items-center justify-content-center",
};

// Search and pagination constants
const SEARCH_CONFIG = {
  MAX_RETRIES: 15,
  RETRY_DELAY_MS: 200,
  RESULTS_PER_PAGE: 48,
  CURRENT_PAGE: 1,
  MAX_VISIBLE_SWATCHES: 4,
  PLACEHOLDER_MIN_HEIGHT: 200,
};

// Error handling utilities
const ErrorHandler = {
  /**
   * Handles specific error types with appropriate recovery strategies
   */
  handleError(error, context, recoveryOptions = {}) {
    const errorType = this._classifyError(error);

    switch (errorType) {
      case "NETWORK_ERROR":
        return this._handleNetworkError(error, context, recoveryOptions);
      case "RENDER_ERROR":
        return this._handleRenderError(error, context, recoveryOptions);
      case "VALIDATION_ERROR":
        return this._handleValidationError(error, context, recoveryOptions);
      default:
        return this._handleGenericError(error, context, recoveryOptions);
    }
  },

  /**
   * Classifies error types for appropriate handling
   * @private
   */
  _classifyError(error) {
    if (error.name === "TypeError" && error.message.includes("fetch")) {
      return "NETWORK_ERROR";
    }
    if (error.message.includes("render") || error.message.includes("DOM")) {
      return "RENDER_ERROR";
    }
    if (error.message.includes("validation") || error.message.includes("invalid")) {
      return "VALIDATION_ERROR";
    }
    return "GENERIC_ERROR";
  },

  /**
   * Handles network-related errors
   * @private
   */
  _handleNetworkError(options) {
    if (options.fallbackToNative) {
      return { shouldFallback: true, retryable: true };
    }
    return { shouldFallback: false, retryable: true };
  },

  /**
   * Handles rendering-related errors
   * @private
   */
  _handleRenderError(options) {
    if (options.showErrorState) {
      return { shouldShowError: true, retryable: false };
    }
    return { shouldFallback: true, retryable: false };
  },

  /**
   * Handles validation errors
   * @private
   */
  _handleValidationError() {
    return { shouldSkip: true, retryable: false };
  },

  /**
   * Handles generic errors
   * @private
   */
  _handleGenericError() {
    return { shouldFallback: true, retryable: false };
  },
};

/**
 * Handles HTML template generation for product cards
 */
class ProductTemplateRenderer {
  constructor(themeConfig, constructorInstance) {
    this.themeConfig = themeConfig;
    this.constructorInstance = constructorInstance;
  }

  /**
   * Generates complete product HTML card
   */
  generateProductHTML(
    product,
    imageData,
    pricingData,
    attributes,
    primaryBadgeResult,
    showSaleBadge,
    saleBadgeColor,
    swatchesResult,
    productIndex = 0,
    isAboveTheFold = false
  ) {
    const { imgWidth, imgHeight, primarySrc, secondarySrc, imageAlt } = imageData;
    const {
      finalPrice,
      isOnSale,
      discountPercentage,
      discountMessage,
      formattedPrice,
      formattedComparePrice,
      fakeDiscountResult,
    } = pricingData;
    const {
      productTitle,
      productHandle,
      productUrl,
      firstVariant,
      cardStyle,
      spacingMobile,
      spacingDesktop,
      imgThumbnail,
      marginClass,
    } = attributes;

    const loadingStrategy = isAboveTheFold ? "eager" : "lazy";
    const fetchPriority = isAboveTheFold ? "high" : "low";

    return `
      <li class="product-item splide__slide ${spacingMobile} ${spacingDesktop} ${marginClass}" 
          data-product-handle="${productHandle}"
          data-cnstrc-item-id="${product.data?.id || product.id}"
          data-cnstrc-item-name="${productTitle}"
          data-cnstrc-item-variation-id="${firstVariant.id || ""}"
          data-cnstrc-item-price="${finalPrice / 100}">
        <div class="${cardStyle || ""}">
          ${this._renderImageSection(
            primarySrc,
            secondarySrc,
            imageAlt,
            imgWidth,
            imgHeight,
            productUrl,
            primaryBadgeResult,
            showSaleBadge,
            discountPercentage,
            saleBadgeColor,
            imgThumbnail,
            loadingStrategy,
            fetchPriority,
            product
          )}
          ${this._renderWishlistSection(productHandle)}
          ${this._renderProductInfo(
            cardStyle,
            productUrl,
            productTitle,
            formattedPrice,
            formattedComparePrice,
            isOnSale,
            discountMessage,
            fakeDiscountResult,
            swatchesResult
          )}
          ${this._renderMobileAddToCart(product)}
        </div>
      </li>
    `;
  }

  /**
   * Renders the image section with badges
   * @private
   */
  _renderImageSection(
    primarySrc,
    secondarySrc,
    imageAlt,
    imgWidth,
    imgHeight,
    productUrl,
    primaryBadgeResult,
    showSaleBadge,
    discountPercentage,
    saleBadgeColor,
    imgThumbnail,
    loadingStrategy,
    fetchPriority,
    product
  ) {
    return `
      <div class="${UI_CLASSES.imgWrapper}${secondarySrc ? " has-hover-image" : ""}">
        <a href="${productUrl}">
          ${this._renderBadges(primaryBadgeResult, showSaleBadge, discountPercentage, saleBadgeColor)}
          ${this._renderImages(
            primarySrc,
            secondarySrc,
            imageAlt,
            imgWidth,
            imgHeight,
            imgThumbnail,
            loadingStrategy,
            fetchPriority
          )}
        </a>
        ${this._renderDesktopAddToCart(product)}
      </div>
    `;
  }

  /**
   * Renders product badges
   * @private
   */
  _renderBadges(primaryBadgeResult, showSaleBadge, discountPercentage, saleBadgeColor) {
    if (primaryBadgeResult.shouldShow) {
      return primaryBadgeResult.badgeHTML;
    }

    if (showSaleBadge && discountPercentage > 0) {
      const saveText = this.themeConfig?.product?.save || "Save";
      return `
        <span class="img-badge-sale badge ${saleBadgeColor} text-white position-absolute" 
              style="top: 10px; left: 10px; z-index: 1;">
          ${saveText} ${discountPercentage}%
        </span>
      `;
    }

    return "";
  }

  /**
   * Renders product images (primary and secondary)
   * @private
   */
  _renderImages(primarySrc, secondarySrc, imageAlt, imgWidth, imgHeight, imgThumbnail, loadingStrategy, fetchPriority) {
    if (!primarySrc) {
      return `
        <div class="${UI_CLASSES.placeholderImage}" 
             style="aspect-ratio: ${imgWidth}/${imgHeight}; min-height: ${SEARCH_CONFIG.PLACEHOLDER_MIN_HEIGHT}px;">
          <span class="text-muted">No Image</span>
        </div>
      `;
    }

    // Use lazy loading with data attributes for below-the-fold images
    const isAboveTheFold = loadingStrategy === "eager";
    const primaryImageAttrs = isAboveTheFold
      ? `src="${primarySrc}"`
      : `data-src="${primarySrc}" src="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='${imgWidth}' height='${imgHeight}'%3E%3C/svg%3E"`;

    const primaryImage = `
      <img 
        ${primaryImageAttrs}
        alt="${imageAlt}"
        class="${UI_CLASSES.productImg} ${imgThumbnail}"
        width="${imgWidth}"
        height="${imgHeight}"
        loading="${loadingStrategy}"
        fetchpriority="${fetchPriority}"
        decoding="async"
      />
    `;

    const secondaryImage = secondarySrc
      ? `
      <img 
        data-src="${secondarySrc}"
        src="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='${imgWidth}' height='${imgHeight}'%3E%3C/svg%3E"
        alt="${imageAlt}"
        class="${UI_CLASSES.productImgSecondary} ${imgThumbnail}"
        width="${imgWidth}"
        height="${imgHeight}"
        loading="lazy"
        fetchpriority="low"
        decoding="async"
      />
    `
      : "";

    return primaryImage + secondaryImage;
  }

  /**
   * Renders wishlist button section
   * @private
   */
  _renderWishlistSection(productHandle) {
    if (!window.theme?.wishlist?.enable || !window.theme?.productCard?.showWishlistBtn) {
      return "";
    }

    const addText = window.theme?.wishlist?.addText || "Add to wishlist";
    const removeText = window.theme?.wishlist?.removeText || "Remove from wishlist";

    return `
      <div class="wishlist-below-image mt-2 d-flex justify-content-end">
        <button class="btn-wishlist-add-remove btn" 
                type="button"
                data-product-handle="${productHandle}"
                data-text-add="${addText}"
                data-text-remove="${removeText}"
                aria-label="${addText}"
                onclick="addOrRemoveFromWishlist(this)"
                data-cnstrc-btn="add_to_wishlist">
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" 
               fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>
          </svg>
        </button>
      </div>
    `;
  }

  /**
   * Renders product information section
   * @private
   */
  _renderProductInfo(
    cardStyle,
    productUrl,
    productTitle,
    formattedPrice,
    formattedComparePrice,
    isOnSale,
    discountMessage,
    fakeDiscountResult,
    swatchesResult
  ) {
    const titleClasses = `${UI_CLASSES.productTitle} ${this.themeConfig?.productCard?.titleFontSizeMobile || "fs-6"} ${
      this.themeConfig?.productCard?.titleFontSizeDesktop || "fs-lg-6"
    } ${this.themeConfig?.productCard?.titleTruncate || ""}`;

    return `
      <div class="firstline-container">
        <div class="${cardStyle === "card" ? "card-body" : ""}">
          <h3 class="${titleClasses}">
            <a href="${productUrl}" class="product-link position-relative d-block link-dark" tabindex="-1">
              ${productTitle}
            </a>
          </h3>
          
          ${this._renderRating()}
          ${this._renderPrice(formattedPrice, formattedComparePrice, isOnSale)}
          ${this._renderDiscountMessage(discountMessage, fakeDiscountResult)}
          ${swatchesResult.shouldShow ? swatchesResult.swatchesHTML : ""}
        </div>
      </div>
    `;
  }

  /**
   * Renders rating section
   * @private
   */
  _renderRating() {
    return window.theme?.productCard?.showRating ? `<div class="product-rating-badge mb-2"></div>` : "";
  }

  /**
   * Renders price section
   * @private
   */
  _renderPrice(formattedPrice, formattedComparePrice, isOnSale) {
    const regularPriceText = this.themeConfig?.product?.priceRegular || "Regular price";
    const salePriceText = this.themeConfig?.product?.priceSale || "Sale price";

    if (isOnSale) {
      return `
        <p class="${UI_CLASSES.productPrice}" data-product-item-price>
          <span class="product-item-price-compare text-muted me-1">
            <span class="visually-hidden">${regularPriceText}&nbsp;</span>
            <s>${formattedComparePrice}</s>
          </span>
          <span class="product-item-price-final">
            <span class="visually-hidden">${salePriceText}&nbsp;</span>
            ${formattedPrice}
          </span>
        </p>
      `;
    } else {
      return `
        <p class="${UI_CLASSES.productPrice}" data-product-item-price>
          <span class="product-item-price-final">
            ${formattedPrice}
          </span>
        </p>
      `;
    }
  }

  /**
   * Renders discount message
   * @private
   */
  _renderDiscountMessage(discountMessage, fakeDiscountResult) {
    if (!discountMessage) return "";

    return `
      <div class="fake-discount-message mt-2 p-2 rounded-2" 
           style="color: ${fakeDiscountResult.messageColor}; font-size: 0.875rem; line-height: 1.2;">
        ${discountMessage}
      </div>
    `;
  }

  /**
   * Renders desktop add-to-cart form
   * @private
   */
  _renderDesktopAddToCart(product) {
    return this.constructorInstance?.getSmartAddToCartForm?.(product, "desktop") || "";
  }

  /**
   * Renders mobile add-to-cart form
   * @private
   */
  _renderMobileAddToCart(product) {
    return this.constructorInstance?.getSmartAddToCartForm?.(product, "mobile") || "";
  }
}

/**
 * Handles image processing and optimization for search results
 */
class ImageProcessor {
  constructor() {
    this._preloadedImages = new Set();
    this._imageCache = new Map();
    this._intersectionObserver = null;
    this._preloadQueue = [];
    this._processingPreloads = false;
    this._initAsyncImageProcessing();
  }

  /**
   * Initialize asynchronous image processing systems
   * @private
   */
  _initAsyncImageProcessing() {
    this._initIntersectionObserver();
    this._setupPreloadBatching();
  }

  /**
   * Sets optimized image parameters on URL object
   * @private
   */
  _setImageParameters(url, width, height, options) {
    if (!url.searchParams.get("width")) url.searchParams.set("width", String(width));
    if (!url.searchParams.get("height")) url.searchParams.set("height", String(height));
    if (!url.searchParams.get("crop")) url.searchParams.set("crop", IMAGE_CONFIG.CROP_CENTER);
    if (!url.searchParams.get("format")) {
      url.searchParams.set("format", IMAGE_CONFIG.FORMATS.WEBP);
    }

    if (options.isAboveTheFold && !url.searchParams.get("quality")) {
      url.searchParams.set("quality", String(PERFORMANCE_CONFIG.IMAGE_QUALITY_OPTIMIZED));
    }
  }

  /**
   * Initialize intersection observer for efficient lazy loading
   * @private
   */
  _initIntersectionObserver() {
    this._intersectionObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const img = entry.target;
            this._loadImageAsync(img);
            this._intersectionObserver.unobserve(img);
          }
        });
      },
      {
        rootMargin: "50px 0px", // Load images 50px before they're visible
        threshold: 0.01,
      }
    );
  }

  /**
   * Setup batched preload processing
   * @private
   */
  _setupPreloadBatching() {
    const processPreloadQueue = () => {
      if (this._processingPreloads || this._preloadQueue.length === 0) return;

      this._processingPreloads = true;
      const batchSize = 3;
      const batch = this._preloadQueue.splice(0, batchSize);

      batch.forEach((src) => this._createPreloadLink(src));

      this._processingPreloads = false;

      if (this._preloadQueue.length > 0) {
        requestIdleCallback(processPreloadQueue, { timeout: 50 });
      }
    };
    requestIdleCallback(processPreloadQueue, { timeout: 100 });
  }

  /**
   * Asynchronously load image when it becomes visible
   * @private
   */
  _loadImageAsync(img) {
    if (img.dataset.src) {
      img.src = img.dataset.src;
      img.removeAttribute("data-src");
    }

    if (img.dataset.srcset) {
      img.srcset = img.dataset.srcset;
      img.removeAttribute("data-srcset");
    }

    this._imageCache.set(img.src, true);
  }

  /**
   * Create preload link asynchronously
   * @private
   */
  _createPreloadLink(src) {
    if (this._preloadedImages.has(src)) return;

    this._preloadedImages.add(src);

    const link = document.createElement("link");
    link.rel = "preload";
    link.as = "image";
    link.href = src;
    link.fetchpriority = "high";

    requestAnimationFrame(() => {
      document.head.appendChild(link);
    });
  }

  /**
   * Queue critical images for asynchronous preloading
   */
  preloadCriticalImage(src) {
    if (this._preloadedImages.has(src)) return;

    this._preloadQueue.push(src);

    if (this._preloadQueue.length <= 2) {
      requestIdleCallback(
        () => {
          const immediateItem = this._preloadQueue.shift();
          if (immediateItem) {
            this._createPreloadLink(immediateItem);
          }
        },
        { timeout: 16 }
      );
    }
  }

  /**
   * Setup lazy loading for product images
   */
  setupLazyLoading(container) {
    if (!this._intersectionObserver) return;

    const lazyImages = container.querySelectorAll("img[data-src]");
    lazyImages.forEach((img) => {
      this._intersectionObserver.observe(img);
    });
  }

  /**
   * Normalizes color names for variant swatches
   */
  normalizeColorName(colorName) {
    if (!colorName) return "";

    const normalized = colorName.trim();
    const compoundColors = ["Rose Gold", "White Gold", "Yellow Gold"];

    for (const compound of compoundColors) {
      if (normalized.toLowerCase().startsWith(compound.toLowerCase())) {
        return compound;
      }
    }

    const materialSuffixes = ["Waterproof Stainless Steel", "Stainless Steel", "Plated", "Filled", "Vermeil"];

    for (const suffix of materialSuffixes) {
      if (normalized.toLowerCase().includes(suffix.toLowerCase())) {
        const baseColor = normalized.split(/\s+(waterproof|stainless|plated|filled|vermeil)/i)[0].trim();
        if (baseColor) return baseColor;
      }
    }

    return normalized;
  }
}

class ConstructorSearch {
  static CONFIG = {
    IMAGE_DIMENSIONS: IMAGE_CONFIG.DIMENSIONS,
    CSS_CLASSES: UI_CLASSES,
    DEFAULTS: {
      maxRetries: SEARCH_CONFIG.MAX_RETRIES,
      retryDelay: SEARCH_CONFIG.RETRY_DELAY_MS,
      resultsPerPage: SEARCH_CONFIG.RESULTS_PER_PAGE,
      currentPage: SEARCH_CONFIG.CURRENT_PAGE,
      maxVisibleSwatches: SEARCH_CONFIG.MAX_VISIBLE_SWATCHES,
      placeholderMinHeight: SEARCH_CONFIG.PLACEHOLDER_MIN_HEIGHT,
      batchSize: PERFORMANCE_CONFIG.BATCH_SIZE_OPTIMIZED,
      initialBatchSize: PERFORMANCE_CONFIG.INITIAL_BATCH_SIZE_OPTIMIZED,
    },
  };

  constructor() {
    if (ConstructorSearch.instance) {
      return ConstructorSearch.instance;
    }

    this.client = null;
    this.isInitialized = false;
    this.isInitializing = false;
    this.currentRequest = null;
    this.currentQuery = "";
    this.currentFilters = {};
    this.retryCount = 0;
    this.maxRetries = SEARCH_CONFIG.MAX_RETRIES;
    this._imageProcessor = null;
    this._templateRenderer = null;

    ConstructorSearch.instance = this;
    this.initializeWhenReady();
  }

  static getInstance() {
    if (!ConstructorSearch.instance) {
      ConstructorSearch.instance = new ConstructorSearch();
    }
    return ConstructorSearch.instance;
  }

  getThemeConfig(path, defaultValue = null) {
    const parts = path.split(".");
    let current = window.theme;
    for (const part of parts) {
      if (current && typeof current === "object" && part in current) {
        current = current[part];
      } else {
        return defaultValue;
      }
    }
    return current;
  }

  getImageDimensions(orientation = null) {
    const imgOrientation = orientation || this.getThemeConfig("productCard.imgOrientation", "square");
    return IMAGE_CONFIG.DIMENSIONS[imgOrientation] || IMAGE_CONFIG.DIMENSIONS.default;
  }

  buildSizedImageUrl(src, width, height, options = {}) {
    if (!src) return "";

    try {
      const url = new URL(src, window.location.origin);
      if (!url.searchParams.get("width")) url.searchParams.set("width", String(width));
      if (!url.searchParams.get("height")) url.searchParams.set("height", String(height));
      if (!url.searchParams.get("crop")) url.searchParams.set("crop", IMAGE_CONFIG.CROP_CENTER);

      if (!url.searchParams.get("format")) {
        url.searchParams.set("format", IMAGE_CONFIG.FORMATS.WEBP);
      }

      if (options.isAboveTheFold && !url.searchParams.get("quality")) {
        url.searchParams.set("quality", String(PERFORMANCE_CONFIG.IMAGE_QUALITY_OPTIMIZED));
      }

      return url.toString();
    } catch (e) {
      const hasQuery = src.includes("?");
      const sep = hasQuery ? "&" : "?";
      const format = `&format=${IMAGE_CONFIG.FORMATS.WEBP}`;
      const quality = options.isAboveTheFold ? `&quality=${PERFORMANCE_CONFIG.IMAGE_QUALITY_OPTIMIZED}` : "";

      return `${src}${sep}width=${width}&height=${height}&crop=${IMAGE_CONFIG.CROP_CENTER}${format}${quality}`;
    }
  }

  /**
   * Transform Constructor.io URLs to match current market domain and locale
   * @param {string} constructorUrl - Original URL from Constructor.io
   * @returns {string} - Transformed URL for current market
   */
  transformUrlForMarket(constructorUrl) {
    if (!constructorUrl) return "";

    const currentHostname = window.location.hostname;
    const currentPathname = window.location.pathname;
    const currentMarket = this.getCurrentMarket();

    try {
      const url = new URL(constructorUrl);

      // Replace domain based on current hostname
      url.hostname = currentHostname;

      // Handle market-specific path prefixes
      if (currentMarket === "canada") {
        // Check if we're on French Canada
        if (currentPathname.startsWith("/fr") || currentHostname.includes("fr.")) {
          if (!url.pathname.startsWith("/fr")) {
            url.pathname = "/fr" + url.pathname;
          }
        }
      }

      return url.toString();
    } catch (error) {
      // If URL parsing fails, try simple string replacement
      console.warn("URL parsing failed, using fallback method:", error);

      let transformedUrl = constructorUrl;

      // Replace domain
      transformedUrl = transformedUrl.replace(/https?:\/\/[^\/]+/, `${window.location.protocol}//${currentHostname}`);

      // Add French prefix for French Canada
      if (currentMarket === "canada" && (currentPathname.startsWith("/fr") || currentHostname.includes("fr."))) {
        transformedUrl = transformedUrl.replace(/\/products\//, "/fr/products/");
      }

      return transformedUrl;
    }
  }

  /**
   * Get current market with enhanced detection
   * @returns {string} - Market identifier
   */
  getCurrentMarket() {
    // Try to get market from theme settings
    if (window.theme?.market?.current) {
      return window.theme.market.current;
    }

    // Try to get market from Constructor.io client
    if (window.constructorioClient?.market) {
      return window.constructorioClient.market;
    }

    // Fallback to URL-based detection
    return this.detectMarketFromUrl() || this.getFallbackMarket();
  }

  /**
   * Get fallback market when primary detection fails
   * @returns {string} - Fallback market identifier
   */
  getFallbackMarket() {
    const hostname = window.location.hostname;
    const pathname = window.location.pathname;

    // More specific market detection
    if (hostname.includes(".ca")) {
      return pathname.startsWith("/fr") || hostname.includes("fr.") ? "canada" : "canada";
    }
    if (hostname.includes(".com.au")) return "australia";
    if (hostname.includes(".co.uk")) return "uk";
    if (hostname.includes(".de")) return "germany";

    return "usa"; // Default fallback
  }

  detectMarketFromUrl() {
    const hostname = window.location.hostname;
    const mappings = {
      ".ca": "canada",
      ".com.au": "australia",
      ".co.uk": "uk",
      ".de": "germany",
    };

    for (const [domain, market] of Object.entries(mappings)) {
      if (hostname.includes(domain)) return market;
    }
    return null;
  }

  preloadCriticalImage(src) {
    if (!this._preloadedImages) this._preloadedImages = new Set();
    if (this._preloadedImages.has(src)) return;

    this._preloadedImages.add(src);

    const link = document.createElement("link");
    link.rel = "preload";
    link.as = "image";
    link.href = src;
    link.fetchpriority = "high";
    document.head.appendChild(link);
  }

  normalizeColorName(colorName) {
    if (!colorName) return "";

    const normalized = colorName.trim();
    const compoundColors = ["Rose Gold", "White Gold", "Yellow Gold"];

    for (const compound of compoundColors) {
      if (normalized.toLowerCase().startsWith(compound.toLowerCase())) {
        return compound;
      }
    }

    const materialSuffixes = ["Waterproof Stainless Steel", "Stainless Steel", "Plated", "Filled", "Vermeil"];

    for (const suffix of materialSuffixes) {
      if (normalized.toLowerCase().includes(suffix.toLowerCase())) {
        const baseColor = normalized.split(/\s+(waterproof|stainless|plated|filled|vermeil)/i)[0].trim();
        if (baseColor) return baseColor;
      }
    }

    return normalized;
  }

  validateProduct(product) {
    if (!product) {
      return { isValid: false, reason: "no_product" };
    }
    if (!product.variants || !Array.isArray(product.variants) || product.variants.length === 0) {
      return { isValid: false, reason: "no_variants" };
    }
    return { isValid: true, reason: null };
  }

  getInventoryDisplayText(variant, showInventory, translations = {}) {
    if (!showInventory) {
      return "";
    }

    if (!variant.available) {
      const soldOutText = this.getThemeConfig("productCard.translations.soldOut", "Sold out");
      return `<small class="text-muted d-block">${soldOutText}</small>`;
    }

    const inventoryQty = variant.inventory_quantity || 0;

    if (inventoryQty <= 0 || inventoryQty >= 10) {
      const inStockText = this.getThemeConfig("productCard.translations.inStock", "In stock");
      return `<small class="text-success d-block">${inStockText}</small>`;
    }

    const stockCountTemplate = this.getThemeConfig("productCard.translations.stockCount", "Only [count] left in stock");
    const stockText = stockCountTemplate.replace("[count]", inventoryQty);
    return `<small class="text-danger d-block">${stockText}</small>`;
  }

  initializeWhenReady() {
    if (this.isInitialized || this.isInitializing) {
      return;
    }
    this.isInitializing = true;

    const checkConstructor = (attempt = 0) => {
      if (window.constructorManager?.isClientReady()) {
        try {
          this.initializeConstructor();
          this.bindEvents();
          this.isInitialized = true;
          this.isInitializing = false;
          return;
        } catch (error) {
          this.isInitializing = false;
        }
      } else if (window.constructorManager || window.constructorio || (window.ConstructorioClient && window.cnstrc?.indexKey)) {
        try {
          this.initializeConstructor();
          this.bindEvents();
          this.isInitialized = true;
          this.isInitializing = false;
          return;
        } catch (error) {
          const errorResult = ErrorHandler.handleError(error, "constructor_initialization", {
            fallbackToNative: false,
            showErrorState: true,
          });
          this.isInitializing = false;
        }
      } else if (attempt >= this.maxRetries) {
        this.isInitializing = false;
        return;
      } else {
        const delay = Math.min(
          PERFORMANCE_CONFIG.INITIAL_RETRY_DELAY_MS * Math.pow(2, attempt),
          PERFORMANCE_CONFIG.MAX_RETRY_DELAY_MS
        );
        setTimeout(() => checkConstructor(attempt + 1), delay);
      }
    };

    checkConstructor();
  }

  initializeConstructor() {
    if (window.constructorManager?.isClientReady()) {
      this.client = window.constructorManager.getClient();
    } else if (window.constructorio) {
      this.client = window.constructorio;
    } else if (window.constructorManager) {
      this.client = window.constructorManager.init();
    } else {
      return;
    }

    if (!this.client) {
      return;
    }

    window.constructorioClient = this.client;

    this.setupEventListeners();
  }

  setupEventListeners() {
    window.addEventListener("cio.client.search.getSearchResults.completed", () => {}, false);

    window.addEventListener(
      "cio.client.search.getSearchResults.error",
      (event) => {
        this.hideLoadingState();
        this.showErrorState();
      },
      false
    );

    window.addEventListener("cio.client.tracker.trackSearchResultsLoaded.completed", () => {}, false);

    window.addEventListener("cio.client.tracker.trackSearchResultClick.completed", () => {}, false);
  }

  /**
   */
  async handleSearch(query, options = {}) {
    if (!this.isInitialized || !this.client) {
      throw new ValidationError("Constructor.io not ready", {
        isInitialized: this.isInitialized,
        hasClient: !!this.client,
      });
    }

    if (this.currentRequest) {
      this.currentRequest.abort();
    }

    this.currentQuery = query;
    this.currentFilters = options.filters || {};

    const urlParams = new URLSearchParams(window.location.search);
    const currentPage = options.page || parseInt(urlParams.get("page")) || ConstructorSearch.CONFIG.DEFAULTS.currentPage;
    const resultsPerPage =
      options.resultsPerPage || parseInt(urlParams.get("per_page")) || ConstructorSearch.CONFIG.DEFAULTS.resultsPerPage;

    this.currentPage = currentPage;

    const controller = new AbortController();
    this.currentRequest = controller;

    try {
      this.showLoadingState();

      const cleanOptions = Object.fromEntries(
        Object.entries(options).filter(([_, value]) => value !== undefined && value !== null && value !== "")
      );

      const searchParams = {
        section: "Products",
        fmt_options: {
          show_hidden_facets: false,
          show_hidden_fields: false,
          fields: [
            "data.handle",
            "data.title",
            "data.price",
            "data.compareAtPrice",
            "data.image_url",
            "data.inventory_quantity",
            "data.group_ids",
            "data.preorder_tag",
            "data.waterproof_tag",
            "data.bestseller_tag",
            "data.new_tag",
            "data.mediaImages",
            "data.url",
            "data.tags",
            "data.vendor",
            "data.product_type",
            "data.options",
            "data.fake_discount_percentage",
            "data.fake_discount_message",
            "data.created_at",
            "data.updated_at",
            "data.published_at",
            "data.created_date",
            "variations",
          ],
        },
        signal: controller.signal,
        ...cleanOptions,
      };

      Object.assign(searchParams, {
        ...(resultsPerPage && { resultsPerPage }),
        ...(options.sortBy && { sortBy: options.sortBy }),
        ...(options.sortOrder && { sortOrder: options.sortOrder }),
        ...(currentPage > 0 && { page: currentPage }),
        ...(Object.keys(this.currentFilters).length > 0 && { filters: this.currentFilters }),
      });

      const response = await this.client.search.getSearchResults(query, searchParams);

      if (controller.signal.aborted) {
        throw new NetworkError("Search request was aborted", { query, aborted: true });
      }

      this.currentRequest = null;

      const results = response.response?.results || response.results || [];
      const totalResults = response.response?.total_num_results || response.total_num_results || results.length;

      const { constructorProducts, productHandles } = results.reduce(
        (acc, result) => {
          const transformedResult = this._transformSearchResult(result);
          if (transformedResult) {
            acc.constructorProducts.push(transformedResult);
            acc.productHandles.push(transformedResult.handle);
          }
          return acc;
        },
        { constructorProducts: [], productHandles: [] }
      );

      await Promise.all([
        this.renderProducts(productHandles, {
          searchTerms: query,
          resultId: response.result_id,
          totalResults,
          constructorProducts,
          currentPage,
          resultsPerPage,
          ...options,
        }),
        Promise.resolve(this.updateURL(query, { page: currentPage, per_page: resultsPerPage })),
        Promise.resolve(
          this.renderPagination({
            currentPage,
            resultsPerPage,
            totalResults,
            query,
          })
        ),
      ]);

      return true;
    } catch (error) {
      const errorResult = await ErrorHandler.handleError(error, "search_request", {
        fallbackToNative: true,
        showErrorState: true,
        query,
        currentPage,
        retryCount: options.retryCount || 0,
      });

      this.currentRequest = null;
      this.hideLoadingState();

      if (errorResult.shouldRetry) {
        const retryOptions = {
          ...options,
          retryCount: (options.retryCount || 0) + 1,
        };

        await new Promise((resolve) => setTimeout(resolve, errorResult.retryDelay));
        return this.handleSearch(query, retryOptions);
      }

      if (errorResult.shouldShowError) {
        this.showErrorState();
      }

      if (!errorResult.shouldFallback) {
        throw error;
      }

      return false;
    }
  }

  /**
   * @private
   */
  /**
   * Helper method to optimize variant transformation
   * @private
   */
  _transformVariant(variant, parentData) {
    const variantTitle = variant.data?.title || variant.value || "Variant";

    let color = variant.data?.color;
    if (!color && variantTitle) {
      const titleParts = variantTitle.split(/[\/,]/).map((s) => s.trim());
      color = titleParts[0];
    }

    return {
      id: variant.data?.variation_id || variant.id,
      title: variantTitle,
      price: Math.round((variant.data?.price || parentData.price || 0) * 100),
      compare_at_price: Math.round((variant.data?.compareAtPrice || parentData.compareAtPrice || 0) * 100),
      inventory_quantity: variant.data?.inventory_quantity || 0,
      available: (variant.data?.inventory_quantity || 0) > 0,
      image_url: variant.data?.image_url,
      color: color,
      size: variant.data?.size,
      discountPercentage: variant.data?.discountPercentage || variant.data?.fake_discount_percentage || 0,
      featured_image: { src: variant.data?.image_url || parentData.image_url || "" },
    };
  }

  _transformSearchResult(item) {
    const handle = this._extractProductHandle(item);
    if (!handle) return null;

    return {
      handle,
      constructorData: item.data,
      variations: item.variations || [],
      value: item.value,
      matchedTerms: item.matched_terms || [],
      isSlotted: item.is_slotted || false,
    };
  }

  /**
   * @private
   */
  _extractProductHandle(item) {
    return (
      item.data?.handle ||
      item.data?.slug ||
      item.data?.shopify_handle ||
      item.data?.url?.split("/products/")[1]?.split("?")[0] ||
      item.value ||
      item.handle ||
      (typeof item.data?.id === "string" && /[a-zA-Z-]/.test(item.data.id) ? item.data.id : null) ||
      (typeof item.data?.item_id === "string" && /[a-zA-Z-]/.test(item.data.item_id) ? item.data.item_id : null)
    );
  }

  async renderProducts(handles, options = {}) {
    if (!handles || handles.length === 0) {
      this.showNoResults(options.searchTerms);
      return;
    }

    try {
      const constructorProducts = options.constructorProducts || [];

      const hasConstructorData = constructorProducts.length > 0;
      let combinedProducts = [];

      if (!hasConstructorData) {
        this.showNoResults(options.searchTerms);
        return;
      }

      combinedProducts = constructorProducts.map((constructorProduct) => {
        const data = constructorProduct.constructorData || {};

        const productTitle = constructorProduct.value || data.title || "Product";

        return {
          id: data.id || constructorProduct.handle,
          handle: constructorProduct.handle,
          title: productTitle,
          price: Math.round((data.price || 0) * 100),
          compare_at_price: Math.round((data.compareAtPrice || 0) * 100),
          featured_image: {
            alt: productTitle,
            src: data.image_url || "",
          },
          image_url: data.image_url || "",
          images: data.mediaImages || [],
          available: (data.inventory_quantity || 0) > 0,
          vendor: data.vendor || "",
          product_type: data.product_type || "",
          options: data.options || [],
          has_only_default_variant: !constructorProduct.variations || constructorProduct.variations.length <= 1,
          variants: constructorProduct.variations?.map((v) => this._transformVariant(v, data)) || [],

          constructorData: constructorProduct.constructorData,
          variations: constructorProduct.variations || [],
          matchedTerms: constructorProduct.matchedTerms || [],
          isSlotted: constructorProduct.isSlotted || false,
          url: this.transformUrlForMarket(data.url || `/products/${constructorProduct.handle}`),
          collections: [],

          tags: [
            ...(data.group_ids || []),
            ...(data.tags ? data.tags.split(",").map((t) => t.trim()) : []),
            ...(data.preorder_tag ? ["preorder"] : []),
            ...(data.waterproof_tag ? ["waterproof"] : []),
            ...(data.bestseller_tag ? ["bestseller"] : []),
            ...(data.new_tag ? ["new"] : []),
            ...(data.low_inventory_tag ? ["low-inventory"] : []),
          ],

          isPreorder: data.preorder_tag || false,
          isWaterproof: data.waterproof_tag || false,
          isBestseller: data.bestseller_tag || false,
          isLowInventory: data.low_inventory_tag || false,
          fakeDiscountPercentage: data.discountPercentage || data.fake_discount_percentage || 0,
          fakeDiscountMessage: data.fake_discount_message || "",
          isNewProduct: data.new_tag || false,
          groupIds: data.group_ids || [],
          constructorPrice: data.price,
          constructorCompareAtPrice: data.compareAtPrice,
        };
      });

      const resultsContainer = this.getResultsContainer();

      if (!resultsContainer) {
        this.showErrorState();
        return;
      }

      this.hideDefaultShopifyResults();
      this.updateResultsMessage(options.searchTerms, options.totalResults);

      await this.renderProductsWithOptimizedDOM(combinedProducts, resultsContainer, options);

      const finalGrid = resultsContainer.querySelector("[data-cnstrc-search]");
      if (finalGrid && options.resultId) {
        finalGrid.setAttribute("data-cnstrc-result-id", options.resultId);
        finalGrid.setAttribute("data-cnstrc-num-results", combinedProducts.length);
      }

      const fallbackPagination = resultsContainer.querySelector(".constructor-fallback-pagination");
      if (fallbackPagination) {
        fallbackPagination.style.display = "none";
      }

      this.reinitializeComponents(resultsContainer);

      this.imageProcessor.setupLazyLoading(resultsContainer);

      this.hideLoadingState();
    } catch (error) {
      const errorResult = ErrorHandler.handleError(error, "product_rendering", {
        fallbackToNative: false,
        showErrorState: true,
      });

      this.hideLoadingState();
      if (errorResult.shouldShowError) {
        this.showErrorState();
      }
    }
  }

  renderProductsTemplate(products, options = {}) {
    if (!products || products.length === 0) {
      return '<li class="col-12"><p class="text-center">No products found.</p></li>';
    }

    const productHTML = products
      .map((product, index) => {
        return this.renderSingleProductTemplate(product, { ...options, productIndex: index });
      })
      .join("");

    return productHTML;
  }

  /**
   * Optimized DOM manipulation using document fragments and efficient rendering
   * @private
   */
  async renderProductsWithOptimizedDOM(products, resultsContainer, options = {}) {
    const existingGrid = resultsContainer.querySelector("[data-cnstrc-search]");

    if (existingGrid) {
      await this.renderProductsInBatchesOptimized(products, existingGrid, options);

      existingGrid.classList.add("constructor-loaded");
    } else {
      const gridElement = this._createGridElement(options, products.length);

      await this.renderProductsInBatchesOptimized(products, gridElement, options);

      resultsContainer.appendChild(gridElement);
    }
  }

  /**
   * Create grid element efficiently without innerHTML
   * @private
   */
  _createGridElement(options, productCount) {
    const gridElement = document.createElement("ul");
    gridElement.className = "product-list list-unstyled row mx-n3 mx-lg-n4 mb-0 constructor-loaded";
    gridElement.setAttribute("data-cnstrc-search", "");
    gridElement.setAttribute("data-cnstrc-search-term", options.searchTerms || "");
    gridElement.setAttribute("data-cnstrc-num-results", productCount.toString());
    gridElement.setAttribute("data-cnstrc-result-id", options.resultId || "");

    return gridElement;
  }

  /**
   * Optimized batch rendering using document fragments
   * @private
   */
  async renderProductsInBatchesOptimized(products, targetElement, options = {}) {
    const batchSize = PERFORMANCE_CONFIG.BATCH_SIZE_OPTIMIZED;
    const initialBatchSize = PERFORMANCE_CONFIG.INITIAL_BATCH_SIZE_OPTIMIZED;

    const hasSkeletonCards = targetElement.querySelector(".constructor-skeleton-item");

    if (hasSkeletonCards) {
      const nonSkeletonChildren = Array.from(targetElement.children).filter(
        (child) => !child.classList.contains("constructor-skeleton-item")
      );
      nonSkeletonChildren.forEach((child) => child.remove());
    } else {
      if (targetElement.firstChild) {
        targetElement.textContent = "";
      }
    }

    if (this._shouldUseVirtualScrolling(products.length)) {
      this._setupVirtualScrolling(products, targetElement, options);
      return;
    }

    if (products.length <= initialBatchSize) {
      const fragment = this._createProductFragment(products, options);
      targetElement.appendChild(fragment);
      this.reinitializeComponents(targetElement.parentElement);
      return;
    }

    const firstBatch = products.slice(0, initialBatchSize);
    const firstFragment = this._createProductFragment(firstBatch, options);
    targetElement.appendChild(firstFragment);

    this._streamRemainingProducts(products.slice(initialBatchSize), targetElement, {
      ...options,
      productIndex: initialBatchSize,
    });
  }

  /**
   * @private
   */
  _createProductFragment(products, options = {}) {
    const fragment = document.createDocumentFragment();
    const tempContainer = document.createElement("div");

    const productHTML = this.renderProductsTemplate(products, options);
    tempContainer.innerHTML = productHTML;

    while (tempContainer.firstChild) {
      fragment.appendChild(tempContainer.firstChild);
    }

    return fragment;
  }

  /**
   * @private
   */
  _streamRemainingProducts(remainingProducts, targetElement, options = {}) {
    const batchSize = PERFORMANCE_CONFIG.BATCH_SIZE_OPTIMIZED;
    let currentIndex = 0;

    const renderNextBatch = () => {
      if (currentIndex >= remainingProducts.length) return;

      const endIndex = Math.min(currentIndex + batchSize, remainingProducts.length);
      const batchProducts = remainingProducts.slice(currentIndex, endIndex);

      const batchOptions = {
        ...options,
        productIndex: (options.productIndex || 0) + currentIndex,
      };

      const fragment = this._createProductFragment(batchProducts, batchOptions);

      this._scheduleDOMUpdate(() => {
        targetElement.appendChild(fragment);
        this.reinitializeComponents(targetElement.parentElement);

        this.imageProcessor.setupLazyLoading(targetElement.parentElement);

        currentIndex = endIndex;
        renderNextBatch();
      });
    };

    renderNextBatch();
  }

  /**
   * @private
   */
  _scheduleDOMUpdate(callback) {
    if (window.requestIdleCallback) {
      window.requestIdleCallback(callback, { timeout: 100 });
    } else {
      setTimeout(callback, 0);
    }
  }

  /**
   * @private
   */
  _shouldUseVirtualScrolling(productCount) {
    return productCount > 100;
  }

  /**
   * @private
   */
  _setupVirtualScrolling(products, targetElement, options = {}) {
    const itemHeight = this._calculateItemHeight(targetElement);
    const containerHeight = window.innerHeight;
    const visibleItems = Math.ceil(containerHeight / itemHeight) + 4;
    const overscan = 2;

    let startIndex = 0;
    let endIndex = Math.min(visibleItems, products.length);
    let isScrolling = false;
    let scrollTimeout;

    const virtualContainer = document.createElement("div");
    virtualContainer.style.cssText = `
      height: ${products.length * itemHeight}px;
      position: relative;
      overflow: hidden;
    `;

    const visibleContainer = document.createElement("div");
    visibleContainer.style.cssText = `
      position: absolute;
      top: 0px;
      left: 0px;
      right: 0px;
      will-change: transform;
    `;

    virtualContainer.appendChild(visibleContainer);
    targetElement.appendChild(virtualContainer);

    this._renderVirtualItems(products.slice(startIndex, endIndex), visibleContainer, options);

    let rafId;
    const scrollHandler = () => {
      if (rafId) return;

      rafId = requestAnimationFrame(() => {
        const scrollTop = targetElement.scrollTop || window.pageYOffset;
        const newStartIndex = Math.max(0, Math.floor(scrollTop / itemHeight) - overscan);
        const newEndIndex = Math.min(products.length, newStartIndex + visibleItems + overscan * 2);

        if (newStartIndex !== startIndex || newEndIndex !== endIndex) {
          startIndex = newStartIndex;
          endIndex = newEndIndex;

          visibleContainer.style.transform = `translateY(${startIndex * itemHeight}px)`;

          this._renderVirtualItems(products.slice(startIndex, endIndex), visibleContainer, {
            ...options,
            productIndex: startIndex,
          });
        }

        rafId = null;
      });
    };

    const scrollStartHandler = () => {
      isScrolling = true;
      clearTimeout(scrollTimeout);
      targetElement.style.pointerEvents = "none";
    };

    const scrollEndHandler = () => {
      scrollTimeout = setTimeout(() => {
        isScrolling = false;
        targetElement.style.pointerEvents = "";
      }, 150);
    };

    const scrollTarget = targetElement.closest(".overflow-auto") || window;

    scrollTarget.addEventListener("scroll", scrollHandler, { passive: true });
    scrollTarget.addEventListener("scroll", scrollStartHandler, { passive: true });
    scrollTarget.addEventListener("scroll", scrollEndHandler, { passive: true });

    return () => {
      if (rafId) {
        cancelAnimationFrame(rafId);
      }
      clearTimeout(scrollTimeout);
      scrollTarget.removeEventListener("scroll", scrollHandler);
      scrollTarget.removeEventListener("scroll", scrollStartHandler);
      scrollTarget.removeEventListener("scroll", scrollEndHandler);

      if (virtualContainer.parentNode) {
        virtualContainer.parentNode.removeChild(virtualContainer);
      }
    };
  }

  /**
   *
   * @private
   */
  _calculateItemHeight(container) {
    const existingCard = container.querySelector(".product-item");
    if (existingCard) {
      const rect = existingCard.getBoundingClientRect();
      return Math.max(300, rect.height);
    }

    const isMobile = window.innerWidth < 768;
    return isMobile ? 350 : 400;
  }

  /**
   * @private
   */
  _renderVirtualItems(products, container, options = {}) {
    const fragment = this._createProductFragment(products, options);
    container.textContent = "";
    container.appendChild(fragment);
    this.reinitializeComponents(container.parentElement);

    this.imageProcessor.setupLazyLoading(container);
  }

  getSmartFakeDiscount(product) {
    const fakeDiscountSettings = window.theme?.fakeDiscount;

    if (!fakeDiscountSettings?.marketSettings?.enabled) {
      return {
        shouldApply: false,
        finalPrice: product.price || 0,
        originalPrice: product.compare_at_price || 0,
        message: null,
      };
    }

    if (product.fakeDiscountPercentage && product.fakeDiscountPercentage > 0) {
      const settings = fakeDiscountSettings?.marketSettings;

      return {
        shouldApply: true,
        finalPrice: Math.round((product.constructorPrice || product.price || 0) * 100),
        originalPrice: Math.round((product.constructorCompareAtPrice || product.compare_at_price || 0) * 100),
        message: settings?.message?.replace(/\[percentage\]/g, Math.round(product.fakeDiscountPercentage)) || "",
        messageColor: settings?.messageColor,
        discountPercentage: Math.round(product.fakeDiscountPercentage),
        discountType: "percentage",
        basePrice: Math.round((product.constructorCompareAtPrice || product.compare_at_price || 0) * 100),
      };
    }

    const settings = fakeDiscountSettings.marketSettings;

    if (!this.productHasTag(product, settings.tag)) {
      return {
        shouldApply: false,
        finalPrice: product.price || 0,
        originalPrice: product.compare_at_price || 0,
        message: null,
      };
    }

    let basePrice = 0;
    const priceSource = fakeDiscountSettings.priceSource || "price";

    if (priceSource === "price") {
      basePrice = product.price || 0;
    } else {
      if (product.compare_at_price && product.compare_at_price > product.price) {
        basePrice = product.compare_at_price;
      } else {
        basePrice = product.price || 0;
      }
    }

    let discountedPrice = 0;
    let actualPercentageForMessage = settings.percentage;

    if (settings.type === "fixed") {
      discountedPrice = parseFloat(settings.fixedAmount || 0) * 100;

      if (basePrice > 0) {
        actualPercentageForMessage = Math.round(((basePrice - discountedPrice) * 100) / basePrice);
      }
    } else {
      const discountAmount = (basePrice * settings.percentage) / 100;
      discountedPrice = basePrice - discountAmount;
      actualPercentageForMessage = settings.percentage;
    }

    let message = settings.message || "";

    message = message.replace(/\[percentage\]/g, settings.percentage);
    message = message.replace(/\[percentage-amount\]/g, actualPercentageForMessage);
    message = message.replace(/\[amount\]/g, settings.fixedAmount || "0");
    message = message.replace(/\[code\]/g, settings.code || "");

    const displayOriginalPrice = product.compare_at_price || product.price || 0;

    const roundedFinalPrice = Math.ceil(discountedPrice);

    return {
      shouldApply: true,
      finalPrice: roundedFinalPrice,
      originalPrice: displayOriginalPrice,
      message: message,
      messageColor: settings.messageColor,
      discountPercentage: actualPercentageForMessage,
      discountType: settings.type || "percentage",
      fixedAmount: settings.fixedAmount,
      basePrice: basePrice,
    };
  }

  productHasTag(product, requiredTag) {
    if (!product.tags || !requiredTag) return false;
    const tags = Array.isArray(product.tags) ? product.tags : (product.tags || "").split(",").map((tag) => tag.trim());
    return tags.includes(requiredTag);
  }

  formatMoney(cents, format) {
    if (typeof cents === "string") {
      cents = cents.replace(".", "");
    }

    cents = Math.round(cents);

    let value = "";
    const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    const formatString = format || window.Shopify?.money_format || "${{amount}}";

    function formatWithDelimiters(number, precision, thousands, decimal) {
      precision = precision || 2;
      thousands = thousands || ",";
      decimal = decimal || ".";

      if (isNaN(number) || number == null) {
        return 0;
      }

      number = (number / 100.0).toFixed(precision);

      const parts = number.split(".");
      const dollarsAmount = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${thousands}`);
      const centsAmount = parts[1] ? decimal + parts[1] : "";

      return dollarsAmount + centsAmount;
    }

    switch (formatString.match(placeholderRegex)[1]) {
      case "amount":
        value = formatWithDelimiters(cents, 2);
        break;
      case "amount_no_decimals":
        value = formatWithDelimiters(cents, 0);
        break;
      case "amount_with_comma_separator":
        value = formatWithDelimiters(cents, 2, ".", ",");
        break;
      case "amount_no_decimals_with_comma_separator":
        value = formatWithDelimiters(cents, 0, ".", ",");
        break;
    }

    return formatString.replace(placeholderRegex, value);
  }

  formatMoneyWithCurrency(cents) {
    const roundedCents = Math.round(cents);
    const currencyCode = window.theme?.fakeDiscount?.currentMarket?.currency || "USD";

    const formatted = new Intl.NumberFormat(window.Shopify?.locale || "en-US", {
      style: "currency",
      currency: currencyCode,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(roundedCents / 100);

    const cleanFormatted = formatted
      .replace(/\s?CA$/, "")
      .replace(/\s?US$/, "")
      .replace(/\s?USD$/, "")
      .replace(/\s?CAD$/, "");

    return cleanFormatted;
  }

  getSmartVariantSwatches(product) {
    const swatchSettings = this.getThemeConfig("variantSwatches");

    if (!swatchSettings?.enabled) {
      return { shouldShow: false, swatchesHTML: null };
    }

    const variations = product.variations || [];
    if (!variations || variations.length <= 1) {
      return { shouldShow: false, swatchesHTML: null };
    }

    const colorGroups = new Map();

    variations.forEach((variation) => {
      const variantData = variation.data || {};

      let color = variantData.color || variation.color;

      if (!color && variantData.title) {
        const titleParts = variantData.title.split(/[\/,]/).map((s) => s.trim());
        color = titleParts[0];
      }

      if (color) {
        const normalizedColor = this.normalizeColorName(color);

        if (!colorGroups.has(normalizedColor)) {
          colorGroups.set(normalizedColor, {
            color: normalizedColor,
            originalColor: color,
            imageUrl: variantData.image_url || product.image_url || product.featured_image?.src || "",
            available: (variantData.inventory_quantity || 0) > 0,
            variation: variation,
          });
        }
      }
    });

    const swatchValues = Array.from(colorGroups.values());
    if (swatchValues.length <= 1) {
      return { shouldShow: false, swatchesHTML: null };
    }

    const maxVisible = swatchSettings.maxVisible || ConstructorSearch.CONFIG.DEFAULTS.maxVisibleSwatches;

    const swatchElements = [];
    let visibleSwatchCount = 0;

    swatchValues.forEach((swatchData) => {
      const { color, imageUrl, available } = swatchData;
      const showSoldOut = swatchSettings.showSoldOut;

      if (!showSoldOut && !available) {
        return;
      }

      const isHidden = visibleSwatchCount >= maxVisible ? "hidden" : "";
      visibleSwatchCount++;

      const swatchImage =
        swatchSettings.useVariantImages && imageUrl
          ? `<img class="img-fluid swatch-variant-image"
             src="${imageUrl}"
             alt="${color}"
             width="40" height="40"
             loading="lazy" aria-hidden="true">`
          : `<img class="img-fluid swatch-custom-image"
             src="https://cdn.shopify.com/s/files/1/0262/9903/9821/files/swatch-${color
               .toLowerCase()
               .replace(/\s+/g, "-")}.png?width=24&height=24&crop=center"
             alt="${color}"
             width="24" height="24"
             loading="lazy" aria-hidden="true"
             onerror="this.style.display='none'">`;

      swatchElements.push(`
        <li class="p-1" ${isHidden}>
          <button class="btn${!available ? " swatch-unavailable" : ""}" type="button"
                  data-img="${imageUrl}"
                  data-img-width="600"
                  data-img-height="600"
                  data-img-thumbnail="${window.theme?.productCard?.imgThumbnail || ""}"
                  data-variant-available="${available}"
                  data-color-value="${color}"
                  onclick="onChangeConstructorProductSwatches(this, event)">
            ${swatchImage}
          </button>
        </li>
      `);
    });

    const showMoreButton = visibleSwatchCount > maxVisible;
    if (showMoreButton) {
      const hiddenCount = visibleSwatchCount - maxVisible;
      swatchElements.push(`
        <li class="p-0">
          <button class="btn btn-view-more" type="button"
                  onclick="showAllConstructorSwatchesInProduct(this, event)"
                  aria-label="+${hiddenCount} more color options">
            +${hiddenCount}
          </button>
        </li>
      `);
    }

    const swatchesHTML = `
      <div class="swatches-container">
        <ul class="color-swatches list-unstyled mx-0 mb-5" aria-label="Color options">
          ${swatchElements.join("")}
        </ul>
      </div>
    `;

    return {
      shouldShow: visibleSwatchCount > 0,
      swatchesHTML: swatchesHTML,
      colorCount: visibleSwatchCount,
    };
  }

  getSmartPrimaryBadge(product, hasDiscount) {
    if (product.isPreorder || this.productHasTag(product, "preorder") || this.productHasTag(product, "pre-order")) {
      return { shouldShow: false, badgeHTML: null, badgeType: "preorder-skip" };
    }

    const promoBadgeSettings = window.theme?.promoBadge;
    if (promoBadgeSettings?.enabled && hasDiscount) {
      return this.getSmartPromoBadge(product);
    }

    const customBadgeSettings = window.theme?.customBadge;
    if (customBadgeSettings?.globalEnabled && customBadgeSettings?.marketSettings?.enabled) {
      return this.getSmartCustomBadge(product);
    }

    const commercialBadgeSettings = window.theme?.commercialBadge;
    if (commercialBadgeSettings?.enabled) {
      return this.getSmartCommercialBadge(product);
    }

    return { shouldShow: false, badgeHTML: null, badgeType: "none" };
  }

  getSmartCommercialBadge(product) {
    const commercialBadgeSettings = window.theme?.commercialBadge;

    if (!commercialBadgeSettings?.enabled) {
      return {
        shouldShow: false,
        badgeHTML: null,
        badgeType: "commercial-disabled",
      };
    }

    let badgeType = null;
    let badgeSettings = null;

    if (product.isWaterproof || this.productHasTag(product, "waterproof")) {
      badgeType = "waterproof";
      badgeSettings = commercialBadgeSettings.waterproof;
    } else if (product.isNewProduct || this.productHasTag(product, "new")) {
      badgeType = "new";
      badgeSettings = commercialBadgeSettings.new;
    } else if (product.isBestseller || this.productHasTag(product, "bestsellers")) {
      badgeType = "bestseller";
      badgeSettings = commercialBadgeSettings.bestseller;
    } else if (this.productHasTag(product, "low-inventory")) {
      badgeType = "lowInventory";
      badgeSettings = commercialBadgeSettings.lowInventory;
    }

    if (!badgeType || !badgeSettings?.text) {
      return {
        shouldShow: false,
        badgeHTML: null,
        badgeType: "commercial-no-match",
      };
    }

    const badgeHTML = `
      <div class="commercial-badge-wrapper position-absolute" style="z-index: 1;">
        <span class="commercial-badge ${badgeType}-badge"
              style="background-color: ${badgeSettings.bgColor}; 
                     color: ${badgeSettings.textColor}; 
                     font-size: ${commercialBadgeSettings.fontSize}px; 
                     border-radius: ${commercialBadgeSettings.borderRadius}px; 
                     opacity: ${commercialBadgeSettings.opacity / 100}; 
                     padding: ${commercialBadgeSettings.padding}px; 
                     width: ${commercialBadgeSettings.width}%; 
                     height: ${commercialBadgeSettings.height}%; 
                     display: inline-flex; 
                     align-items: center; 
                     justify-content: center;
                     font-weight: 500;
                     text-transform: uppercase;
                     line-height: 1.2;
                     margin: 1rem;">
          ${badgeSettings.text}
        </span>
      </div>
    `;

    return {
      shouldShow: true,
      badgeHTML: badgeHTML,
      badgeType: `commercial-${badgeType}`,
    };
  }

  getSmartPromoBadge(product) {
    const promoBadgeSettings = window.theme?.promoBadge;

    if (!promoBadgeSettings?.enabled) {
      return {
        shouldShow: false,
        badgeHTML: null,
        badgeType: "promo-disabled",
      };
    }

    const variant = product.variants?.[0] || product;
    const compareAtPrice = variant.compare_at_price || product.compare_at_price || 0;
    const price = variant.price || product.price || 0;

    if (!compareAtPrice || compareAtPrice <= price) {
      return { shouldShow: false, badgeHTML: null, badgeType: "no-discount" };
    }

    const savingsPercentage = Math.round(((compareAtPrice - price) * 100) / compareAtPrice);

    const remainder = savingsPercentage % 5;
    const roundedPercentage = remainder > 2 ? savingsPercentage + 5 - remainder : savingsPercentage - remainder;

    const opacity = (promoBadgeSettings.opacity || 100) / 100;
    const bgColor = promoBadgeSettings.bgColor || "#ff0000";

    const rgbMatch = bgColor.match(/^#([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i);
    const r = rgbMatch ? parseInt(rgbMatch[1], 16) : 255;
    const g = rgbMatch ? parseInt(rgbMatch[2], 16) : 0;
    const b = rgbMatch ? parseInt(rgbMatch[3], 16) : 0;

    const badgeHTML = `
      <div class="promo-badge-wrapper position-absolute" style="top: 10px; left: 10px;">
        <span class="promo-badge badge"
              style="padding: ${promoBadgeSettings.padding || 8}px;
                     width: ${promoBadgeSettings.width || 60}px;
                     height: ${promoBadgeSettings.height || 60}px;
                     font-size: ${promoBadgeSettings.fontSize || 12}px;
                     background-color: rgba(${r}, ${g}, ${b}, ${opacity});
                     color: ${promoBadgeSettings.textColor || "#ffffff"};
                     border-radius: ${promoBadgeSettings.borderRadius || 50}px;
                     display: inline-flex;
                     align-items: center;
                     justify-content: center;
                     font-weight: 500;
                     text-transform: uppercase;
                     line-height: 1.2;">
          ${roundedPercentage}% OFF
        </span>
      </div>
    `;

    return {
      shouldShow: true,
      badgeHTML: badgeHTML,
      badgeType: "promo",
      discountPercentage: roundedPercentage,
    };
  }

  getSmartCustomBadge(product) {
    const customBadgeSettings = window.theme?.customBadge;

    if (!customBadgeSettings?.globalEnabled || !customBadgeSettings?.marketSettings?.enabled) {
      return {
        shouldShow: false,
        badgeHTML: null,
      };
    }

    const settings = customBadgeSettings.marketSettings;

    const badgeHTML = `
      <div class="custom-badge-wrapper position-absolute" style=":top: 10px; left: 10px;">
        <span class="custom-badge badge"
              style="background-color: ${settings.bgColor}; 
                     color: ${settings.textColor}; 
                     font-size: ${settings.fontSize}px; 
                     border-radius: ${settings.borderRadius}px; 
                     opacity: ${settings.opacity / 100}; 
                     padding: ${settings.padding}px; 
                     width: ${settings.width}px; 
                     height: ${settings.height}px; 
                     display: flex; 
                     align-items: center; 
                     justify-content: center;">
          ${settings.text}
        </span>
      </div>
    `;

    return {
      shouldShow: true,
      badgeHTML: badgeHTML,
    };
  }

  buildProductImageData(product, isAboveTheFold = false) {
    const { width: imgWidth, height: imgHeight } = this.getImageDimensions();
    const imageOptions = { isAboveTheFold };

    const featuredImage = product.featured_image || "";
    const fallbackImageUrl = featuredImage ? this.buildSizedImageUrl(featuredImage, imgWidth, imgHeight, imageOptions) : "";
    const mediaImages = Array.isArray(product.constructorData?.mediaImages) ? product.constructorData.mediaImages : [];

    const ctorPrimary = product.constructorData?.image_url || "";
    let primarySrcRaw = ctorPrimary || fallbackImageUrl;

    if (!primarySrcRaw && mediaImages.length > 0) {
      primarySrcRaw = mediaImages[0]?.transformedSrc || "";
    }

    const getPathname = (src) => {
      try {
        return new URL(src, window.location.origin).pathname;
      } catch (e) {
        return (src || "").split("?")[0];
      }
    };

    const primaryPath = getPathname(primarySrcRaw);
    const differentMedia = mediaImages.find((img) => {
      if (!img?.transformedSrc) return false;
      return getPathname(img.transformedSrc) !== primaryPath;
    });

    const secondarySrcRaw = differentMedia?.transformedSrc || null;
    const primarySrc = primarySrcRaw ? this.buildSizedImageUrl(primarySrcRaw, imgWidth, imgHeight, imageOptions) : "";
    const secondarySrc = secondarySrcRaw
      ? this.buildSizedImageUrl(secondarySrcRaw, imgWidth, imgHeight, { isAboveTheFold: false })
      : null;

    // Preload critical above-the-fold images
    if (isAboveTheFold && primarySrc) {
      this.preloadCriticalImage(primarySrc);
    }

    return {
      imgWidth,
      imgHeight,
      primarySrc,
      secondarySrc,
      imageAlt: product.title || "",
    };
  }

  calculateProductPricing(product) {
    const firstVariant = (product.variants && product.variants[0]) || {};
    const fakeDiscountResult = this.getSmartFakeDiscount(product);

    let finalPrice, originalPrice, isOnSale, discountPercentage, discountMessage;

    if (fakeDiscountResult.shouldApply) {
      finalPrice = fakeDiscountResult.finalPrice;
      originalPrice = fakeDiscountResult.originalPrice;
      isOnSale = true;
      discountPercentage = fakeDiscountResult.discountPercentage;
      discountMessage = fakeDiscountResult.message;
    } else {
      finalPrice = firstVariant.price || 0;
      originalPrice = firstVariant.compare_at_price || 0;
      isOnSale = originalPrice && originalPrice > finalPrice;
      discountPercentage = isOnSale ? Math.round(((originalPrice - finalPrice) / originalPrice) * 100) : 0;
      discountMessage = null;
    }

    const formattedPrice = this.formatMoneyWithCurrency(finalPrice);
    const formattedComparePrice = isOnSale && originalPrice ? this.formatMoneyWithCurrency(originalPrice) : "";

    return {
      finalPrice,
      originalPrice,
      isOnSale,
      discountPercentage,
      discountMessage,
      formattedPrice,
      formattedComparePrice,
      fakeDiscountResult,
    };
  }

  buildProductAttributes(product) {
    const productTitle = product.title || "";
    const productHandle = product.handle || "";

    // Transform Constructor.io URL for current market
    let productUrl = product.url || `/products/${productHandle}`;
    productUrl = this.transformUrlForMarket(productUrl);

    const firstVariant = (product.variants && product.variants[0]) || {};
    const isAvailable = product.available || false;

    const cardStyle = this.getThemeConfig("productCard.style");
    const spacingMobile = this.getThemeConfig("productCard.spacing.mobile")
      ? `p-${this.getThemeConfig("productCard.spacing.mobile")}`
      : "p-3";
    const spacingDesktop = this.getThemeConfig("productCard.spacing.desktop")
      ? `p-lg-${this.getThemeConfig("productCard.spacing.desktop")}`
      : "p-lg-4";
    const imgThumbnail = this.getThemeConfig("productCard.imgThumbnail", "");
    const marginClass = cardStyle !== "card" ? "mb-4" : "";

    return {
      productTitle,
      productHandle,
      productUrl,
      firstVariant,
      isAvailable,
      cardStyle,
      spacingMobile,
      spacingDesktop,
      imgThumbnail,
      marginClass,
    };
  }

  renderSingleProductTemplate(product, options = {}) {
    const validation = this.validateProduct(product);
    if (!validation.isValid) return "";

    const productIndex = options.productIndex || 0;
    const isAboveTheFold = productIndex < PERFORMANCE_CONFIG.ABOVE_FOLD_THRESHOLD;

    const imageData = this.buildProductImageData(product, isAboveTheFold);
    const pricingData = this.calculateProductPricing(product);
    const attributes = this.buildProductAttributes(product);

    const primaryBadgeResult = this.getSmartPrimaryBadge(product, pricingData.discountPercentage > 0);
    const showSaleBadge = window.theme?.productCard?.showSaleBadge && pricingData.isOnSale;
    const saleBadgeColor = window.theme?.productCard?.saleBadgeColor || "primary";

    // Defer swatches rendering for below-the-fold products
    const swatchesResult = isAboveTheFold ? this.getSmartVariantSwatches(product) : { shouldShow: false, swatchesHTML: null };

    return this.generateProductHTML(
      product,
      imageData,
      pricingData,
      attributes,
      primaryBadgeResult,
      showSaleBadge,
      saleBadgeColor,
      swatchesResult,
      productIndex,
      isAboveTheFold
    );
  }

  generateProductHTML(
    product,
    imageData,
    pricingData,
    attributes,
    primaryBadgeResult,
    showSaleBadge,
    saleBadgeColor,
    swatchesResult,
    productIndex = 0,
    isAboveTheFold = false
  ) {
    const { imgWidth, imgHeight, primarySrc, secondarySrc, imageAlt } = imageData;
    const {
      finalPrice,
      isOnSale,
      discountPercentage,
      discountMessage,
      formattedPrice,
      formattedComparePrice,
      fakeDiscountResult,
    } = pricingData;
    const {
      productTitle,
      productHandle,
      productUrl,
      firstVariant,
      cardStyle,
      spacingMobile,
      spacingDesktop,
      imgThumbnail,
      marginClass,
    } = attributes;

    const loadingStrategy = isAboveTheFold ? "eager" : "lazy";
    const fetchPriority = isAboveTheFold ? "high" : "low";

    return `
      <li class="product-item splide__slide ${spacingMobile} ${spacingDesktop} ${marginClass}" 
          data-product-handle="${productHandle}"
          data-cnstrc-item-id="${product.data?.id || product.id}"
          data-cnstrc-item-name="${productTitle}"
          data-cnstrc-item-variation-id="${firstVariant.id || ""}"
          data-cnstrc-item-price="${finalPrice / 100}">
        <div class="${cardStyle || ""}">
          <div class="${UI_CLASSES.imgWrapper}${secondarySrc ? " has-hover-image" : ""}">
            <a href="${productUrl}">
              ${
                primaryBadgeResult.shouldShow
                  ? primaryBadgeResult.badgeHTML
                  : showSaleBadge && discountPercentage > 0
                  ? `<span class="img-badge-sale badge ${saleBadgeColor} text-white position-absolute" style="top: 10px; left: 10px; z-index: 1;">${
                      window.theme?.product?.save || "Save"
                    } ${discountPercentage}%</span>`
                  : ""
              }
              ${
                primarySrc
                  ? `<img src="${primarySrc}" alt="${imageAlt}" class="${
                      UI_CLASSES.productImg
                    } ${imgThumbnail}" width="${imgWidth}" height="${imgHeight}" loading="${loadingStrategy}" fetchpriority="${fetchPriority}" decoding="async"/>${
                      secondarySrc
                        ? `<img src="${secondarySrc}" alt="${imageAlt}" class="${UI_CLASSES.productImgSecondary} ${imgThumbnail}" width="${imgWidth}" height="${imgHeight}" loading="lazy" fetchpriority="low" decoding="async"/>`
                        : ""
                    }`
                  : `<div class="${UI_CLASSES.placeholderImage}" style="aspect-ratio: ${imgWidth}/${imgHeight}; min-height: ${SEARCH_CONFIG.PLACEHOLDER_MIN_HEIGHT}px;"><span class="text-muted">No Image</span></div>`
              }
            </a>
            ${this.getSmartAddToCartForm(product, "desktop")}
          </div>
          ${
            window.theme?.wishlist?.enable && window.theme?.productCard?.showWishlistBtn
              ? `<div class="wishlist-below-image mt-2 d-flex justify-content-end"><button class="btn-wishlist-add-remove btn" type="button" data-product-handle="${productHandle}" data-text-add="${
                  window.theme?.wishlist?.addText || "Add to wishlist"
                }" data-text-remove="${window.theme?.wishlist?.removeText || "Remove from wishlist"}" aria-label="${
                  window.theme?.wishlist?.addText || "Add to wishlist"
                }" onclick="addOrRemoveFromWishlist(this)" data-cnstrc-btn="add_to_wishlist"><svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path></svg></button></div>`
              : ""
          }
          <div class="firstline-container">
            <div class="${cardStyle === "card" ? "card-body" : ""}">
              <h3 class="${UI_CLASSES.productTitle} ${this.getThemeConfig(
      "productCard.titleFontSizeMobile",
      "fs-6"
    )} ${this.getThemeConfig("productCard.titleFontSizeDesktop", "fs-lg-6")} ${this.getThemeConfig(
      "productCard.titleTruncate",
      ""
    )}">
                <a href="${productUrl}" class="product-link position-relative d-block link-dark" tabindex="-1">${productTitle}</a>
              </h3>
              ${window.theme?.productCard?.showRating ? `<div class="product-rating-badge mb-2"></div>` : ""}
              <p class="${UI_CLASSES.productPrice}" data-product-item-price>
                ${
                  isOnSale
                    ? `<span class="product-item-price-compare text-muted me-1"><span class="visually-hidden">${this.getThemeConfig(
                        "product.priceRegular",
                        "Regular price"
                      )}&nbsp;</span><s>${formattedComparePrice}</s></span><span class="product-item-price-final"><span class="visually-hidden">${this.getThemeConfig(
                        "product.priceSale",
                        "Sale price"
                      )}&nbsp;</span>${formattedPrice}</span>`
                    : `<span class="product-item-price-final">${formattedPrice}</span>`
                }
              </p>
              ${
                discountMessage
                  ? `<div class="fake-discount-message mt-2 p-2 rounded-2" style="color: ${fakeDiscountResult.messageColor}; font-size: 0.875rem; line-height: 1.2;">${discountMessage}</div>`
                  : ""
              }
              ${swatchesResult.shouldShow ? swatchesResult.swatchesHTML : ""}
            </div>
          </div>
          ${this.getSmartAddToCartForm(product, "mobile")}
        </div>
      </li>
    `;
  }

  getResultsContainer() {
    let container = document.querySelector("#constructor-search-results");
    if (container) {
      return container;
    }

    container = document.querySelector("[data-cnstrc-search]");
    if (container && container.parentElement) {
      return container.parentElement;
    }

    container = document.querySelector("#search .container");
    if (container) {
      return container;
    }
    return null;
  }

  updateResultsMessage(searchTerms, totalResults) {
    const messageElement = document.querySelector(".search-results-message");
    if (messageElement && searchTerms) {
      const message =
        window.searchPageTranslations?.resultsWithCountAndTerm || `Found ${totalResults} results for "${searchTerms}"`;
      messageElement.textContent = message.replace("COUNT_PLACEHOLDER", totalResults).replace("TERMS_PLACEHOLDER", searchTerms);

      messageElement.classList.remove("constructor-hide-until-ready");
    }
  }

  showLoadingState() {
    const container = this.getResultsContainer();

    if (!container) return;

    container.classList.add("constructor-loading");

    let existingGrid = container.querySelector("[data-cnstrc-search]");

    if (existingGrid) {
      existingGrid.classList.add("constructor-replacing");

      const skeletonCards = existingGrid.querySelectorAll(".constructor-skeleton-item");

      if (skeletonCards.length > 0) {
        skeletonCards.forEach((card) => {
          card.style.display = "";
          card.classList.remove("d-none");
        });
      }
    } else {
      existingGrid = document.createElement("ul");
      existingGrid.className = "product-list list-unstyled row mx-n3 mx-lg-n4 mb-0";
      existingGrid.setAttribute("data-cnstrc-search", "");
      container.appendChild(existingGrid);
      existingGrid.classList.add("constructor-replacing");
    }
  }

  hideLoadingState() {
    const container = this.getResultsContainer();
    if (!container) return;

    container.classList.remove("constructor-loading");

    const existingGrid = container.querySelector("[data-cnstrc-search]");
    if (existingGrid) {
      existingGrid.classList.remove("constructor-replacing");

      existingGrid.classList.add("constructor-loaded");

      // when .constructor-loaded class is applied, allowing for smooth transitions
    }

    const spinner = container.querySelector(".constructor-spinner");
    if (spinner) {
      spinner.remove();
    }
  }

  showErrorState() {
    const container = this.getResultsContainer();
    if (container) {
      const errorEl = document.createElement("div");
      errorEl.className = "alert alert-warning py-3";
      errorEl.setAttribute("role", "status");
      errorEl.textContent = "Search temporarily unavailable. Please try again.";

      const existingGrid = container.querySelector("[data-cnstrc-search]");
      if (existingGrid) {
        existingGrid.classList.add("constructor-loaded");
        existingGrid.replaceWith(errorEl);
      }
    }

    this.hideLoadingState();
    this.hidePagination();
  }

  hideDefaultShopifyResults() {
    document.body.classList.add("constructor-search-active");
  }

  showNoResults(searchTerms) {
    const container = this.getResultsContainer();
    if (container) {
      const noResultsEl = document.createElement("p");
      noResultsEl.className = "alert alert-warning py-3";
      noResultsEl.setAttribute("role", "status");
      noResultsEl.setAttribute("data-cnstrc-zero-result", "");
      noResultsEl.textContent = `No results found for "${searchTerms}"`;

      const existingGrid = container.querySelector("[data-cnstrc-search]");
      if (existingGrid) {
        existingGrid.classList.add("constructor-loaded");
        existingGrid.replaceWith(noResultsEl);
      }
    }

    this.hideLoadingState();
  }

  reinitializeComponents(container) {
    document.dispatchEvent(
      new CustomEvent("constructor:products-loaded", {
        detail: { container },
      })
    );

    this.initializeProductTracking(container);

    if (window.ProductCard) {
      container.querySelectorAll(".product-item").forEach((card) => {
        new ProductCard(card);
      });
    }
  }

  initializeProductTracking(container) {
    const productItems = container.querySelectorAll(".product-item");
    productItems.forEach((item) => {
      const productLink = item.querySelector('a[href*="/products/"]');
      if (productLink && window.constructorio?.tracker) {
        productLink.removeEventListener("click", this.handleProductClick);

        productLink.addEventListener("click", () => {});
      }
    });
  }

  updateURL(query, paginationParams = {}) {
    const url = new URL(window.location);
    url.searchParams.set("q", query);
    url.searchParams.set("type", "product");

    if (paginationParams.page && paginationParams.page > 1) {
      url.searchParams.set("page", paginationParams.page);
    } else {
      url.searchParams.delete("page");
    }

    if (paginationParams.per_page && paginationParams.per_page !== 24) {
      url.searchParams.set("per_page", paginationParams.per_page);
    } else {
      url.searchParams.delete("per_page");
    }

    window.history.pushState(null, "", url.toString());
  }

  renderPagination(options) {
    const { currentPage, resultsPerPage, totalResults, query } = options;
    const totalPages = Math.ceil(totalResults / resultsPerPage);

    if (totalPages <= 1) {
      this.hidePagination();
      return;
    }

    let paginationContainer = document.querySelector(".constructor-pagination");
    if (!paginationContainer) {
      const resultsContainer = this.getResultsContainer();
      if (resultsContainer) {
        paginationContainer = document.createElement("div");
        paginationContainer.className = "constructor-pagination d-flex justify-content-center mt-4";
        resultsContainer.appendChild(paginationContainer);
      }
    }

    if (!paginationContainer) return;

    const paginationHTML = this.generatePaginationHTML(currentPage, totalPages, query, resultsPerPage);
    paginationContainer.innerHTML = paginationHTML;

    this.bindPaginationEvents(paginationContainer);

    paginationContainer.style.display = "flex";
  }

  generatePaginationHTML(currentPage, totalPages, query, resultsPerPage) {
    let html = '<nav aria-label="Search results pagination"><ul class="pagination mb-0">';

    if (currentPage > 1) {
      html += `
        <li class="page-item">
          <a class="page-link constructor-page-link" href="#" data-page="${
            currentPage - 1
          }" data-query="${query}" data-per-page="${resultsPerPage}">
            <span aria-hidden="true">&laquo;</span>
            <span class="sr-only">Previous</span>
          </a>
        </li>
      `;
    } else {
      html += `
        <li class="page-item disabled">
          <span class="page-link">
            <span aria-hidden="true">&laquo;</span>
            <span class="sr-only">Previous</span>
          </span>
        </li>
      `;
    }

    const startPage = Math.max(1, currentPage - 2);
    const endPage = Math.min(totalPages, currentPage + 2);

    if (startPage > 1) {
      html += `<li class="page-item"><a class="page-link constructor-page-link" href="#" data-page="1" data-query="${query}" data-per-page="${resultsPerPage}">1</a></li>`;
      if (startPage > 2) {
        html += `<li class="page-item disabled"><span class="page-link">...</span></li>`;
      }
    }

    for (let i = startPage; i <= endPage; i++) {
      if (i === currentPage) {
        html += `<li class="page-item active"><span class="page-link">${i}</span></li>`;
      } else {
        html += `<li class="page-item"><a class="page-link constructor-page-link" href="#" data-page="${i}" data-query="${query}" data-per-page="${resultsPerPage}">${i}</a></li>`;
      }
    }

    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        html += `<li class="page-item disabled"><span class="page-link">...</span></li>`;
      }
      html += `<li class="page-item"><a class="page-link constructor-page-link" href="#" data-page="${totalPages}" data-query="${query}" data-per-page="${resultsPerPage}">${totalPages}</a></li>`;
    }

    if (currentPage < totalPages) {
      html += `
        <li class="page-item">
          <a class="page-link constructor-page-link" href="#" data-page="${
            currentPage + 1
          }" data-query="${query}" data-per-page="${resultsPerPage}">
            <span aria-hidden="true">&raquo;</span>
            <span class="sr-only">Next</span>
          </a>
        </li>
      `;
    } else {
      html += `
        <li class="page-item disabled">
          <span class="page-link">
            <span aria-hidden="true">&raquo;</span>
            <span class="sr-only">Next</span>
          </span>
        </li>
      `;
    }

    html += "</ul></nav>";
    return html;
  }

  bindPaginationEvents(container) {
    const pageLinks = container.querySelectorAll(".constructor-page-link");
    pageLinks.forEach((link) => {
      link.addEventListener("click", (e) => {
        e.preventDefault();
        const page = parseInt(link.dataset.page);
        const query = link.dataset.query;
        const perPage = parseInt(link.dataset.perPage);

        const resultsContainer = this.getResultsContainer();
        if (resultsContainer) {
          resultsContainer.scrollIntoView({
            behavior: "smooth",
            block: "start",
          });
        }

        this.handleSearch(query, { page: page, resultsPerPage: perPage });
      });
    });
  }

  hidePagination() {
    const paginationContainer = document.querySelector(".constructor-pagination");
    if (paginationContainer) {
      paginationContainer.style.display = "none";
    }
  }

  bindEvents() {
    const forms = [];

    const mainForm = document.getElementById("main-search-form");
    if (mainForm) {
      forms.push({ form: mainForm, type: "main-search" });
    }

    const offcanvasForm = document.querySelector("[data-cnstrc-search-form]");
    if (offcanvasForm && offcanvasForm !== mainForm) {
      forms.push({ form: offcanvasForm, type: "offcanvas-search" });
    }

    const genericForms = document.querySelectorAll('form[action*="/search"]');
    genericForms.forEach((form) => {
      if (!forms.some((f) => f.form === form)) {
        forms.push({ form: form, type: "generic-search" });
      }
    });

    if (forms.length > 0) {
      forms.forEach(({ form }) => {
        form.removeEventListener("submit", this.handleFormSubmit, true);
        form.removeEventListener("submit", this.handleFormSubmit, false);
        form.addEventListener("submit", this.handleFormSubmit.bind(this), true);

        const originalOnSubmit = form.onsubmit;
        form.onsubmit = (e) => {
          if (e.target !== form) {
            Object.defineProperty(e, "target", {
              value: form,
              enumerable: true,
            });
            Object.defineProperty(e, "currentTarget", {
              value: form,
              enumerable: true,
            });
          }

          this.handleFormSubmit(e);

          if (!e.defaultPrevented) {
            return true;
          }

          if (originalOnSubmit && typeof originalOnSubmit === "function") {
            return originalOnSubmit.call(form, e);
          }
          return false;
        };

        const submitBtn = form.querySelector('button[type="submit"], input[type="submit"]');

        if (submitBtn) {
          submitBtn.onclick = (e) => {
            e.preventDefault();
            e.stopPropagation();

            const formEvent = new Event("submit", {
              bubbles: true,
              cancelable: true,
            });
            Object.defineProperty(formEvent, "target", {
              value: form,
              enumerable: true,
            });
            Object.defineProperty(formEvent, "currentTarget", {
              value: form,
              enumerable: true,
            });

            this.handleFormSubmit(formEvent);
            return false;
          };
        } else {
        }
      });
    } else {
    }

    document.addEventListener(
      "click",
      (e) => {
        if (e.target.tagName === "BUTTON") {
          if (e.target.className.includes("btn-primary") && e.target.textContent?.trim().includes("Search")) {
          }
        }
      },
      true
    );

    document.addEventListener("change", (e) => {
      if (e.target.matches("[data-constructor-filter]")) {
        this.handleFilterChange();
      }
    });

    window.addEventListener("popstate", () => {
      const urlParams = new URLSearchParams(window.location.search);
      const query = urlParams.get("q");
      const page = parseInt(urlParams.get("page")) || 1;
      const perPage = parseInt(urlParams.get("per_page")) || 24;

      if (query && (query !== this.currentQuery || page !== this.currentPage)) {
        this.handleSearch(query, { page: page, resultsPerPage: perPage });
      }
    });
  }

  /**
   * ✅ Modern form submission with enhanced validation and error handling
   */
  async handleFormSubmit(e) {
    try {
      // ✅ Modern form data extraction with validation
      const formData = new FormData(e.target);
      const searchTerm = formData.get("q")?.trim();
      const searchType = formData.get("type");

      // ✅ Early validation with custom error types
      if (!searchTerm) {
        throw new ValidationError("Search term is required", { searchTerm });
      }

      if (searchTerm.length < 2) {
        throw new ValidationError("Search term must be at least 2 characters", {
          searchTerm,
          length: searchTerm.length,
        });
      }

      // ✅ Modern context detection
      const isOffcanvas = e.target.closest("#offcanvas-search") !== null;
      const isSearchPage = window.location.pathname.includes("/search");

      // Handle offcanvas behavior
      if (isOffcanvas && searchTerm && !isSearchPage) {
        return;
      }

      // ✅ Prevent default behavior
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();

      // ✅ Enhanced type checking with modern patterns
      const isProductSearch = !searchType || searchType.includes("product");

      if (isProductSearch) {
        const success = await this.handleSearch(searchTerm);

        if (!success) {
          this.fallbackToNativeSearch(e.target);
        }
      } else {
        this.fallbackToNativeSearch(e.target);
      }
    } catch (error) {
      const errorResult = await ErrorHandler.handleError(error, "form_submission", {
        showErrorState: true,
        fallbackToNative: true,
      });

      if (errorResult.shouldShowError) {
        this._showFormError(e.target, error.message);
      }

      if (errorResult.shouldFallback) {
        this.fallbackToNativeSearch(e.target);
      }
    }
  }

  /**
   * @private
   */
  _showFormError(form, message) {
    const existingError = form.querySelector(".search-error-message");
    if (existingError) {
      existingError.remove();
    }

    const errorElement = document.createElement("div");
    errorElement.className = "search-error-message alert alert-danger mt-2";
    errorElement.setAttribute("role", "alert");
    errorElement.textContent = message;

    // Insert after form
    form.parentNode.insertBefore(errorElement, form.nextSibling);

    // Auto-remove after 5 seconds
    setTimeout(() => {
      if (errorElement.parentNode) {
        errorElement.remove();
      }
    }, 5000);
  }

  fallbackToNativeSearch(form) {
    form.removeEventListener("submit", this.handleFormSubmit, true);
    form.removeEventListener("submit", this.handleFormSubmit, false);

    form.submit();
  }

  handleFilterChange() {
    const filters = this.gatherFilters();
    if (this.currentQuery) {
      this.handleSearch(this.currentQuery, { filters });
    }
  }

  gatherFilters() {
    return {};
  }

  getSmartAddToCartForm(product, deviceType = "desktop") {
    const settings = this.getThemeConfig("productCard");

    if (!settings?.showAtcForm) {
      return "";
    }

    const isCustomizedProduct =
      product.collections?.some((collection) => collection.handle === "customized") ||
      product.tags?.some((tag) => tag === "customized");

    if (isCustomizedProduct) {
      return this.generateViewProductButton(product, deviceType);
    }

    const validation = this.validateProduct(product);
    if (!validation.isValid) {
      return "";
    }

    const availableVariants = product.variants.filter((variant) => variant.available);

    if (!product.available || availableVariants.length === 0) {
      return this.generateSoldOutButton(deviceType);
    }

    if (product.has_only_default_variant || availableVariants.length === 1) {
      return this.generateSimpleAddToCartButton(product, availableVariants[0], deviceType);
    }

    return this.generateDropdownAddToCartButton(product, availableVariants, deviceType);
  }

  getSmartAddToCartFormMobile(product) {
    return this.getSmartAddToCartForm(product, "mobile");
  }

  generateViewProductButton(product, deviceType = "desktop") {
    let productUrl = product.url || `/products/${product.handle}`;
    productUrl = this.transformUrlForMarket(productUrl);
    const isMobile = deviceType === "mobile";

    const containerClass = isMobile
      ? ConstructorSearch.CONFIG.CSS_CLASSES.containerMobile
      : ConstructorSearch.CONFIG.CSS_CLASSES.containerAtc;
    const buttonClass = isMobile ? "btn-vp w-100 d-block" : "view-product-btn-desktop";
    const buttonId = isMobile ? "new-view-product-btn-mobile" : "btn-vp-desktop";
    const wrapperDiv = isMobile ? "" : '<div class="mb-2">';
    const wrapperDivEnd = isMobile ? "" : "</div>";

    return `
      <div class="${containerClass}">
        ${wrapperDiv}
          <a href="${productUrl}" 
             class="${buttonClass}"
             id="${buttonId}"
             role="button">
            ${this.getThemeConfig("productCard.translations.viewProduct", "View Product")}
          </a>
        ${wrapperDivEnd}
      </div>
    `;
  }

  generateSoldOutButton(deviceType = "desktop") {
    const isMobile = deviceType === "mobile";

    const containerClass = isMobile
      ? ConstructorSearch.CONFIG.CSS_CLASSES.containerMobile
      : ConstructorSearch.CONFIG.CSS_CLASSES.containerAtc;
    const wrapperDiv = isMobile ? "" : '<div class="mb-2">';
    const wrapperDivEnd = isMobile ? "" : "</div>";
    const buttonId = isMobile ? "" : 'id="btn-atc-desktop"';

    return `
      <div class="${containerClass}">
        ${wrapperDiv}
          <button class="${ConstructorSearch.CONFIG.CSS_CLASSES.btnAtc}" ${buttonId} disabled>
            ${this.getThemeConfig("productCard.translations.soldOut", "Sold Out")}
          </button>
        ${wrapperDivEnd}
      </div>
    `;
  }

  generateSimpleAddToCartButton(product, variant, deviceType = "desktop") {
    const isMobile = deviceType === "mobile";

    let productUrl = product.url || `/products/${product.handle}`;
    productUrl = this.transformUrlForMarket(productUrl);

    const containerClass = isMobile
      ? ConstructorSearch.CONFIG.CSS_CLASSES.containerMobile
      : ConstructorSearch.CONFIG.CSS_CLASSES.containerAtc;
    const wrapperDiv = isMobile ? "" : '<div class="mb-2">';
    const wrapperDivEnd = isMobile ? "" : "</div>";
    const buttonId = isMobile ? "" : 'id="btn-atc-desktop"';
    const addToCartText = isMobile
      ? this.getThemeConfig("productCard.translations.addToCart", "Add to cart")
      : this.getThemeConfig("productCard.translations.addToCart", "Add To Cart");

    return `
      <div class="${containerClass}">
        ${wrapperDiv}
          <form class="shopify-product-form product-item-form"
                onsubmit="onSubmitAtcForm(this, event)"
                data-product-id="${product.id}"
                data-product-title="${product.title}"
                data-product-url="${productUrl}">
            <input type="hidden" name="id" value="${variant.id}">
            <button class="${ConstructorSearch.CONFIG.CSS_CLASSES.btnAtc}"
                    type="submit"
                    ${buttonId}
                    name="add"
                    data-text-add-to-cart="${addToCartText}">
              ${addToCartText}
            </button>
          </form>
        ${wrapperDivEnd}
      </div>
    `;
  }

  generateDropdownAddToCartButton(product, availableVariants, deviceType = "desktop") {
    const translations = window.theme?.productCard?.translations;
    const showInventory = window.theme?.productCard?.formShowInventory;
    const isMobile = deviceType === "mobile";

    let productUrl = product.url || `/products/${product.handle}`;
    productUrl = this.transformUrlForMarket(productUrl);

    const dropdownItems = availableVariants
      .map((variant) => this.generateVariantDropdownItem(variant, showInventory, translations, deviceType))
      .join("");

    const containerClass = isMobile ? "mobile-view d-block d-lg-none" : "btn-atc-container";
    const wrapperDiv = isMobile ? "" : '<div class="mb-2">';
    const wrapperDivEnd = isMobile ? "" : "</div>";
    const dropdownClass = isMobile ? "w-100 dropup" : "w-200 dropup";
    const buttonClass = isMobile ? "btn-atc w-100" : "btn-atc btn btn-md dropdown-toggle btn-primary w-200";
    const buttonId = isMobile ? "new-add-to-bag-btn-mobile" : "btn-atc-desktop";
    const menuClass = isMobile ? "dropdown-menu shadow w-100" : "dropdown-menu shadow w-200";
    const addToCartText = isMobile ? translations?.addToCart || "Add to cart" : translations?.addToCart || "Add To Cart";

    return `
      <div class="${containerClass}">
        ${wrapperDiv}
          <form class="shopify-product-form product-item-form"
                onsubmit="onSubmitAtcForm(this, event)"
                data-product-id="${product.id}"
                data-product-title="${product.title}"
                data-product-url="${productUrl}">
            <input type="hidden" name="id" value="${availableVariants[0].id}">
            <div class="${dropdownClass}">
              <button type="button"
                      class="${buttonClass}"
                      id="${buttonId}"
                      data-bs-toggle="dropdown"
                      data-text-add-to-cart="${addToCartText}"
                      aria-expanded="false">
                ${addToCartText}
              </button>
              <ul class="${menuClass}">
                ${dropdownItems}
              </ul>
            </div>
          </form>
        ${wrapperDivEnd}
      </div>
    `;
  }

  generateVariantDropdownItem(variant, showInventory, translations, deviceType = "desktop") {
    const inventoryDisplay = this.getInventoryDisplayText(variant, showInventory, translations);
    const isMobile = deviceType === "mobile";
    const flexClass = isMobile
      ? "d-flex flex-column flex-lg-row justify-content-lg-between align-items-lg-center lh-sm"
      : "d-flex flex-column justify-content-start lh-sm";

    return `
      <li class="${variant.inventory_policy || ""}">
        <button class="dropdown-item"
                onclick="onClickAtcFormVariant(this, event)"
                data-variant-id="${variant.id}"
                type="button"
                data-cnstrc-btn="add_to_cart"
                ${!variant.available ? "disabled" : ""}>
          <div class="${flexClass}">
            <span class="small">${variant.title}</span>
            ${inventoryDisplay}
          </div>
        </button>
      </li>
    `;
  }
}

window.onChangeConstructorProductSwatches = (btn, event) => {
  event.preventDefault();
  event.stopPropagation();

  const productItem = btn.closest(".product-item");
  if (!productItem) {
    return;
  }

  const allSwatchesInProduct = productItem.querySelectorAll(".color-swatches .btn");
  allSwatchesInProduct.forEach((swatch) => swatch.classList.remove("selected"));

  btn.classList.add("selected");

  const productImg = productItem.querySelector(".product-item-img");
  if (productImg && btn.dataset.img) {
    productImg.src = btn.dataset.img;

    if (btn.dataset.imgWidth) productImg.width = btn.dataset.imgWidth;
    if (btn.dataset.imgHeight) productImg.height = btn.dataset.imgHeight;
  }
};

window.showAllConstructorSwatchesInProduct = (btn, event) => {
  event.preventDefault();
  event.stopPropagation();

  const swatchesList = btn.closest(".color-swatches");
  if (!swatchesList) {
    return;
  }

  swatchesList.querySelectorAll("li[hidden]").forEach((li) => {
    li.removeAttribute("hidden");
  });

  btn.remove();
};

function initializeConstructorSearch() {
  const shouldInitialize =
    window.cnstrc?.isSearchPage ||
    window.cnstrc?.searchEnabled ||
    document.getElementById("search") ||
    document.querySelector("[data-cnstrc-search]") ||
    document.querySelector("[data-cnstrc-search-form]");

  if (shouldInitialize) {
    if (ConstructorSearch.instance?.isInitialized || ConstructorSearch.instance?.isInitializing) {
      return;
    }

    const instance = getConstructorSearchInstance();
    if (instance) {
      instance.hideDefaultShopifyResults();
    }
  }
}

if (!window.constructorSearchInitialized) {
  window.constructorSearchInitialized = true;

  const initializeWhenReady = () => {
    initializeConstructorSearch();
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initializeWhenReady, { once: true });
  } else {
    initializeWhenReady();
  }
}
